"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-8">
        <div className="h-3 w-16 bg-muted animate-pulse" />
        <div className="h-3 w-20 bg-muted animate-pulse" />
        <div className="h-3 w-24 bg-muted animate-pulse" />
        <div className="h-3 w-16 bg-muted animate-pulse" />
      </div>;
  }
  return <ul className="flex items-center justify-end gap-8 list-none m-0 p-0">
      {menuItems.map((item, i) => <li key={item.id} data-index={i} className="relative group">
          <Link to={item.href || "#"} className="text-foreground font-default uppercase font-medium text-[13px] tracking-[0.04em] leading-none relative inline-block py-1">
            <span className="relative">
              {item.label}
              <span className="absolute left-0 -bottom-1 h-px w-0 group-hover:w-full transition-all duration-[220ms] ease-out" style={{
            backgroundColor: "var(--chart-3)"
          }} aria-hidden="true" />
            </span>
          </Link>
        </li>)}
    </ul>;
}
function MobileNavConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12">
        <div className="h-8 w-40 bg-muted animate-pulse" />
        <div className="h-8 w-32 bg-muted animate-pulse" />
        <div className="h-8 w-48 bg-muted animate-pulse" />
        <div className="h-8 w-28 bg-muted animate-pulse" />
      </div>;
  }
  return <ul className="flex flex-col gap-8 list-none m-0 p-0 mt-12">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} onClick={onNavigate} className="text-foreground font-default text-2xl md:text-3xl font-medium tracking-tight leading-tight inline-block">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="10742" id="header" className={`sticky top-0 left-0 right-0 z-50 w-full transition-all duration-[240ms] ease-out bg-background text-foreground ${scrolled ? "bg-background/80 backdrop-blur-lg border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="w-full max-w-[1440px] mx-auto px-5 md:px-6 lg:px-12 py-5 md:py-6">
        <div className="grid grid-cols-12 gap-4 md:gap-6 items-center">
          {}
          <div className="col-span-6 md:col-span-3">
            <Link to="/HomePage" className="inline-block text-foreground font-default uppercase font-semibold text-[18px] leading-none" style={{
            letterSpacing: "-0.02em"
          }} aria-label="Water Service Provider Guide — Home">
              WATER SERVICE PROVIDER GUIDE
            </Link>
          </div>

          {}
          <div className="hidden md:block md:col-span-6" aria-hidden="true" />

          {}
          <div className="hidden md:flex md:col-span-3 justify-end">
            <MenuProvider menu_id="33">
              <DesktopNavConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="col-span-6 flex justify-end md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="text-foreground font-default uppercase font-medium text-[13px] tracking-[0.04em] h-auto px-0 py-1 rounded-md hover:bg-transparent" aria-label="Open menu">
                  <Menu className="h-4 w-4 mr-2" aria-hidden="true" />
                  <span>MENU</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background text-foreground w-full sm:max-w-full border-0 p-8">
                <SheetTitle className="text-foreground font-default uppercase font-medium text-[13px] tracking-[0.04em]">
                  Navigation
                </SheetTitle>
                <nav aria-label="Mobile navigation" className="text-foreground">
                  <MenuProvider menu_id="33">
                    <MobileNavConsumer onNavigate={() => setMobileOpen(false)} />
                  </MenuProvider>
                </nav>
                <div className="mt-auto pt-8 border-t border-foreground/15">
                  <p className="font-default uppercase text-[13px] tracking-[0.04em] text-foreground/60">
                    Water Service Provider Guide
                  </p>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}